import firebase from "firebase"
import {db, storage} from "../db";
import Events from "./Events";

// global err function
function err(e) {
  return {success: false, error: e};
}

/* Upload an image/file to Firebase Storage */
async function uploadImage(file) {
  var storageRef = firebase.storage().refFromURL("gs://" + storage + "/template_images")
  var fileRef = storageRef.child(file.name);

  return new Promise((resolve, reject) => {
    fileRef.put(file).then((snapshot) => {
      var percentage = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
      if (percentage == 100) {
        fileRef.getDownloadURL()
          .then(url => {
            resolve({success: true, url: url});
          })
          .catch(e => reject(err(e)));
      } else {
        resolve({success: false, url: ''});
      }
    })
      .catch(e => reject(err(e)));
  });
}

/* Create template */
async function createTemplate(name, tags, questions, description) {
  return new Promise(async (resolve, reject) => {
    await db.collection("Template").doc().set({
      name,
      name_lower: name.toLowerCase(),
      tags,
      questions,
      description,
    })
    .then(() => resolve({success: true}))
    .catch(e => reject(err(e)));
  })
}

/* Watch all templates */
function watchAllTemplates(callback) {
  const unsubscribe = db.collection("Template")
    .orderBy("name_lower", 'asc')
    .onSnapshot(querySnapshot => {
      if (querySnapshot.empty) {
        callback({success: true, data: []});
      } else {
        let templates = [];
        querySnapshot.forEach(doc => {
          let newDoc = {
            sID: doc.id,
            questions: doc.data().questions,
            name: doc.data().name,
            description: doc.data().description,
            tags: doc.data().tags || [],
          };
          templates.push(newDoc);
        });
        callback({success: true, data: templates});
      }
    }, e => callback(err(e)));
  return unsubscribe;
}

async function getTemplate(sID) {
  return new Promise((resolve, reject) => {
    db.collection("Template").doc(sID).get()
      .then(docSnapshot => {
        if (docSnapshot.exists) {
          const template = {
            sID: docSnapshot.id,
            questions: docSnapshot.data().questions,
            name: docSnapshot.data().name,
            description: docSnapshot.data().description,
            tags: docSnapshot.data().tags || [],
          };
          resolve({success: true, data: template});
        } else {
          resolve({success: false, data: {}});
        }
      })
      .catch(e => reject(err(e)));
  })
}

async function updateTemplate(sID, name, tags, questions, description) {
  return new Promise(async (resolve, reject) => {
    await db.collection("Template").doc(sID).update({
      name,
      name_lower: name.toLowerCase(),
      tags,
      questions,
      description,
    })
    .then(() => resolve({success: true}))
    .catch(e => reject(err(e)));
  })
}

async function deleteTemplate(tID) {
  return new Promise(async (resolve, reject) => {
    db.collection("Template").doc(tID).delete()
      .then(() => resolve({success: true}))
      .catch(e => reject(err(e)));
  });
}

/* Create Survey */
async function createSurvey(name, tags, questions, description, gameID, developerID, status = 0) {
  return new Promise(async (resolve, reject) => {
    const docRef = db.collection("Survey").doc();
    const tID = docRef.id;
    await docRef.set({
      name,
      name_lower: name.toLowerCase(),
      tags,
      questions,
      description,
      gameID,
      developerID,
      status,
    })
    .then(() => {
      Events.logEvent("survey.created", { surveyId: tID, gameId: gameID, developerID });
      resolve({success: true, data: tID});
    })
    .catch(e => reject(err(e)));
  });
}

/* Watch All Surveys (Admin) */
function watchAllSurveys(callback) {
  const unsubscribe = db.collection("Survey")
    .orderBy("name_lower", 'asc')
    .onSnapshot(querySnapshot => {
      if (querySnapshot.empty) {
        callback({success: true, data: []})
      } else {
        let surveys = [];
        querySnapshot.forEach(doc => {
          let newDoc = {
            sID: doc.id,
            name: doc.data().name,
            tags: doc.data().tags || [],
            questions: doc.data().questions,
            description: doc.data().description,
            gameID: doc.data().gameID,
            developerID: doc.data().developerID,
            status: doc.data().status,
            feedback: doc.data().feedback,
            reviewRequest: doc.data().reviewRequest,
            platform: doc.data().platform || [],
            behavior: doc.data().behavior || [],
            genre: doc.data().genre || [],
          };
          surveys.push(newDoc);
        });
        surveys.sort((a, b) => {
          const aTop = a.status === 3 ? 0 : 1;
          const bTop = b.status === 3 ? 0 : 1;
          if (aTop !== bTop) return aTop - bTop;
          return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
        });
        callback({ success: true, data: surveys });
      }
    }, e => callback(err(e)));
  return unsubscribe;
}

/* Get All Surveys (Admin) */
async function getAllSurveys() {
  return new Promise((resolve, reject) => {
    db.collection("Survey").orderBy("name_lower", 'asc').get()
      .then(querySnapshot => {
        if (querySnapshot.empty) {
          resolve({success: true, data: []});
        } else {
          let surveys = [];
          querySnapshot.forEach(doc => {
            let newDoc = {
              sID: doc.id,
              name: doc.data().name,
              tags: doc.data().tags || [],
              questions: doc.data().questions,
              description: doc.data().description,
              gameID: doc.data().gameID,
              developerID: doc.data().developerID,
              status: doc.data().status,
              feedback: doc.data().feedback,
              reviewRequest: doc.data().reviewRequest,
              platform: doc.data().platform || [],
              behavior: doc.data().behavior || [],
              genre: doc.data().genre || [],
            };
            surveys.push(newDoc);
          });
          resolve({success: true, data: surveys});
        }
      })
      .catch(e => reject(err(e)));
  });
}

/* Get surveys for a specific developer */
async function getSurveysOfDev(devID) {
  return new Promise((resolve, reject) => {
    db.collection("Survey")
      .where("developerID", "==", devID)
      .orderBy("name_lower", 'asc')
      .get()
      .then(querySnapshot => {
        if (querySnapshot.empty) {
          resolve({success: true, data: []});
        } else {
          let surveys = [];
          querySnapshot.forEach(doc => {
            let newDoc = {
              sID: doc.id,
              name: doc.data().name,
              tags: doc.data().tags || [],
              questions: doc.data().questions,
              description: doc.data().description,
              gameID: doc.data().gameID,
              developerID: doc.data().developerID,
              status: doc.data().status,
              feedback: doc.data().feedback,
              reviewRequest: doc.data().reviewRequest,
              platform: doc.data().platform || [],
              behavior: doc.data().behavior || [],
              genre: doc.data().genre || [],
            };
            surveys.push(newDoc);
          });
          resolve({success: true, data: surveys});
        }
      })
      .catch(e => reject(err(e)));
  });
}

/* Notifications */
export async function sendSurveyCreatedNotification(gameId, surveyId) {
  try {
    const surveyDoc = await db.collection("Survey").doc(surveyId).get();
    const survey = surveyDoc.data();
    const gameDoc = await db.collection("Game").doc(gameId).get();
    const game = gameDoc.data();
    const testerIDs = game.testerIDs || [];
    if (!testerIDs.length) return;
    const notification = {
      type: "surveyCreated",
      projectId: gameId,
      projectName: game.gameName || "Project",
      projectImg: game.gameImage || game.coverImage || game.img || null,
      surveyId,
      surveyName: survey.name || "Survey",
      timestamp: new Date(),
      unread: true,
    };
    const batch = db.batch();
    testerIDs.forEach((tid) => {
      const ref = db.collection("UserNotifications").doc(tid).collection("Notifications").doc();
      batch.set(ref, notification);
    });
    await batch.commit();
  } catch (e) { console.error(e); }
}

/* Get All Games (Tester Discover) */
async function getAllGamesForTester(testerId) {
  return new Promise((resolve, reject) => {
    db.collection("Game").orderBy("gameName", 'asc').get()
      .then(querySnapshot => {
        if (querySnapshot.empty) {
          resolve({ success: true, data: [] });
        } else {
          let games = [];
          querySnapshot.forEach(doc => {
            let testerIds = doc.data().testerIDs || [];
            if (!testerIds.includes(testerId)) {
              let newDoc = {
                gameID: doc.id,
                img: doc.data().img,
                name: doc.data().gameName,
                tags: doc.data().tags || [],
                description: doc.data().description,
                testerIDs: doc.data().testerIDs || [],
                developerID: doc.data().developerID,
                platform: doc.data().platform || [],
                behavior: doc.data().behavior || [],
                genre: doc.data().genre || [],
              };
              games.push(newDoc)
            }
          })
          resolve({ success: true, data: games })
        }
      })
      .catch(e => reject(err(e)))
  })
}

/* Get Surveys by Game ID */
async function getSurveysByGameID(gameID) {
  return new Promise((resolve, reject) => {
    db.collection("Survey").where("gameID", "==", gameID).get()
      .then(querySnapshot => {
        if (querySnapshot.empty) resolve({success: true, data: []});
        else {
          let surveys = [];
          querySnapshot.forEach(doc => {
            let newDoc = {
              sID: doc.id,
              name: doc.data().name,
              tags: doc.data().tags || [],
              questions: doc.data().questions,
              description: doc.data().description,
              gameID: doc.data().gameID,
              developerID: doc.data().developerID,
              status: doc.data().status,
              platform: doc.data().platform || [],
              behavior: doc.data().behavior || [],
              genre: doc.data().genre || [],
            };
            surveys.push(newDoc);
          });
          resolve({success: true, data: surveys});
        }
      })
      .catch(e => reject(err(e)));
  })
}

/* Watch Surveys by Game ID */
function watchSurveysByGameID(gameID, callback) {
  const unsubscribe = db.collection("Survey")
    .where("gameID", "==", gameID)
    .orderBy("name_lower", 'asc')
    .onSnapshot(querySnapshot => {
      if (querySnapshot.empty) callback({success: true, data: []});
      else {
        let surveys = [];
        querySnapshot.forEach(doc => {
          let newDoc = {
            sID: doc.id,
            name: doc.data().name,
            tags: doc.data().tags || [],
            questions: doc.data().questions,
            description: doc.data().description,
            gameID: doc.data().gameID,
            developerID: doc.data().developerID,
            status: doc.data().status,
            platform: doc.data().platform || [],
            behavior: doc.data().behavior || [],
            genre: doc.data().genre || [],
          };
          surveys.push(newDoc);
        });
        callback({success: true, data: surveys});
      }
    }, e => callback(err(e)));
  return unsubscribe;
}

/* Watch Surveys by Developer ID */
function watchSurveysByDevID(devID, callback) {
  const unsubscribe = db.collection("Survey")
    .where("developerID", "==", devID)
    .orderBy("name_lower", 'asc')
    .onSnapshot(querySnapshot => {
      if (querySnapshot.empty) callback({success: true, data: []});
      else {
        let surveys = [];
        querySnapshot.forEach(doc => {
          let newDoc = {
            sID: doc.id,
            name: doc.data().name,
            tags: doc.data().tags || [],
            questions: doc.data().questions,
            description: doc.data().description,
            gameID: doc.data().gameID,
            developerID: doc.data().developerID,
            status: doc.data().status,
            platform: doc.data().platform || [],
            behavior: doc.data().behavior || [],
            genre: doc.data().genre || [],
          };
          surveys.push(newDoc);
        })
        callback({success: true, data: surveys});
      }
    }, e => callback(err(e)));
  return unsubscribe;
}

async function getSurvey(sID) {
  return new Promise((resolve, reject) => {
    db.collection("Survey").doc(sID).get()
      .then(docSnapshot => {
        if (docSnapshot.exists) {
          const survey = {
            sID: docSnapshot.id,
            name: docSnapshot.data().name,
            tags: docSnapshot.data().tags || [],
            questions: docSnapshot.data().questions,
            description: docSnapshot.data().description,
            gameID: docSnapshot.data().gameID,
            developerID: docSnapshot.data().developerID,
            status: docSnapshot.data().status,
            platform: docSnapshot.data().platform || [],
            behavior: docSnapshot.data().behavior || [],
            genre: docSnapshot.data().genre || [],
          };
          resolve({success: true, data: survey});
        } else {
          resolve({success: false, data: {}});
        }
      })
      .catch(e => reject(err(e)));
  })
}

async function updateSurvey(sID, name, tags, questions, description) {
  return new Promise(async (resolve, reject) => {
    await db.collection("Survey").doc(sID).update({
      name,
      name_lower: name.toLowerCase(),
      tags,
      questions,
      description,
    })
    .then(() => resolve({success: true , data: sID}))
    .catch(e => reject(err(e)));
  });
}

async function setSurveyStatus(sID, status) {
  return new Promise((resolve, reject) => {
    db.collection("Survey").doc(sID).update({ status })
      .then(() => resolve({success: true}))
      .catch(e => reject(err(e)))
  })
}

async function setSurveyStatusAndFeedback(sID, status, feedback) {
  return new Promise((resolve, reject) => {
    db.collection("Survey").doc(sID).update({ status, feedback })
    .then(() => resolve({ success: true }))
    .catch(e => reject(err(e)));
  });
}

async function setSurveyStatusRemoveFeedback(sID, status) {
  return new Promise((resolve, reject) => {
    db.collection("Survey").doc(sID).update({
      status,
      feedback: firebase.firestore.FieldValue.delete(),
    })
    .then(() => resolve({ success: true }))
    .catch(e => reject(err(e)));
  });
}

async function setSurveyStatusAndReviewRequest(sID, status, reviewRequest) {
  return new Promise((resolve, reject) => {
    db.collection("Survey").doc(sID).update({ status, reviewRequest })
    .then(() => resolve({ success: true }))
    .catch(e => reject(err(e)));
  });
}

async function setSurveyStatusRemoveReviewRequest(sID, status) {
  return new Promise((resolve, reject) => {
    db.collection("Survey").doc(sID).update({
      status,
      reviewRequest: firebase.firestore.FieldValue.delete(),
    })
    .then(() => resolve({ success: true }))
    .catch(e => reject(err(e)));
  });
}

async function clearFlaggedQuestions(sID) {
  return new Promise((resolve, reject) => {
    const docRef = db.collection("Survey").doc(sID);
    docRef.get().then(docSnap => {
        if (!docSnap.exists) return reject({ success: false, error: "survey doesn't exist" });
        const updated = docSnap.data().questions.map(q => ({ ...q, flagged: false }));
        return docRef.update({ questions: updated }).then(() => resolve({ success: true }));
      }).catch(e => reject(err(e)));
  });
}

async function deleteSurvey(sID) {
  return new Promise((resolve, reject) => {
    db.collection("Survey").doc(sID).delete()
      .then(() => resolve({success: true}))
      .catch(e => reject(err(e)));
  });
}

export default {
  uploadImage,
  createTemplate,
  watchAllTemplates,
  getTemplate,
  updateTemplate,
  deleteTemplate,
  createSurvey,
  watchAllSurveys,
  getAllSurveys,
  getSurveysOfDev,
  getSurveysByGameID,
  getAllGamesForTester,
  watchSurveysByGameID,
  watchSurveysByDevID,
  getSurvey,
  updateSurvey,
  setSurveyStatus,
  setSurveyStatusAndFeedback,
  setSurveyStatusRemoveFeedback,
  setSurveyStatusAndReviewRequest,
  setSurveyStatusRemoveReviewRequest,
  clearFlaggedQuestions,
  deleteSurvey,
  sendSurveyCreatedNotification
}


// WEBPACK FOOTER //
// ./src/Firestore/Template.js